import { useState, useEffect, useRef, useCallback } from "react";
import HiddenAOReport from "./HiddenAOReport";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";
import { useLocation } from "react-router-dom";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  navy:  "#A80D2C",
  blue:  "#C4163A",
  teal:  "#7B0F2B",
  gold:  "#D79A1E",
  slate: "#5B677A",
  bg:    "#F5F8FC",
  card:  "#FFFFFF",
  line:  "#DCE5EF",
  ink:   "#1D2733",
  orange: "#F97316" ,
};

// ─── AO CATEGORY DEFINITIONS ──────────────────────────────────────────────────
const AO_DEFINITIONS = [
  {
    key: "AR",
    label: "Achievement Related",
    tagline: '"I set my own goals and strive to achieve them."',
    overview:
      "Participants who score high on this aspect have a high level of Achievement Motivation. They set their own goals and try their best to achieve them. They love facing more and more challenges in their career and have a high level of reliability with regard to their work. These participants are the most important assets to the organisation for its growth.",
    orgNote:
      "It is difficult to hold these people in the organisation with only monetary incentives. After a certain level, salary takes a back seat in their list of priorities. The only way to hold them is to provide maximum opportunities of career development as well as personal development. They like freedom in their work and authority to take their own decisions. Given the freedom to work in their own style, they will not let the organisation down — they will exceed expectations.",
    toDo: [
      "Keep at it. Look for challenges.",
      "Create situations of challenge — otherwise you may get bored.",
      "Understand that due to your level of achievement motivation you will require freedom to work, which may or may not be possible in your work environment.",
      "However, do not reduce your level of achievement motivation.",
    ],
  },
  {
    key: "TR",
    label: "Task Related",
    tagline: '"Reliable, structured, and target-driven."',
    overview:
      "These people have a high level of reliability in their work. They make sure that the work allocated to them is completed within given timelines under any circumstances. They do not mind working under strict supervision and can be given targets. These people are important to organisations for their reliability.",
    orgNote:
      "There could be some participants who are high on AR and TR at the same time. These people act as per the situations in work life. At entry-level positions they could be TR, and as they move up the ladder and gain more opportunities and freedom, they become AR.",
    toDo: [
      "Seek independence.",
      "Seek challenges and achieve your targets before time.",
      "Stretch your targets beyond the level given to you by your supervisor.",
    ],
  },
  {
    key: "UR",
    label: "Unrelated to Achievement",
    tagline: '"Comfort-zone focused, avoiding challenges."',
    overview:
      "These people are only concerned with their earnings. They are least interested in their work and would try to avoid challenges as far as possible. They do not set any goals for their career or their organisation. They are least reliable in their work life and, as a result, are most redundant in any organisation.",
    orgNote:
      "Remaining in the UR zone is a cause for concern. The first step is awareness — recognising this pattern and making a conscious decision to move toward challenge-seeking behaviour.",
    toDo: [
      "Take charge of your assignment.",
      "Try and create a winner's instinct — move away from the comfort zone. Initially look for mini challenges and mini stretch goals.",
      "Monitor your work and review the results actually obtained vs. the desired level of interest.",
      "Remaining in the UR zone is a cause for concern.",
    ],
  },
];

// ── The 4th, combined profile — shown only when someone scores High on both AR and TR
const AR_TR_COMBINED = {
  key: "AR_TR",
  label: "High AR & TR",
  tagline: '"Act as per the requirements at the workplace."',
  overview:
    "The participants act as per the requirements at the workplace. Normally, they start with Task Related and gradually go on to Achievement Related.",
  toDo: [
    "Be conscious of your need to be reliable at the workplace and also your need for achievement of independence at your workplace.",
    "This profile is an excellent profile.",
  ],
};

// ─── CATEGORY PROFILE LOGIC ───────────────────────────────────────────────────
function getCategoryLabel(scores) {
  if (!scores) return "—";
  const { AR = 0, TR = 0, UR = 0 } = scores;
  const isHighAR = AR > 12;
  const isHighTR = TR >= 6;
  if (isHighAR && isHighTR) return "High AR & TR";
  if (isHighAR) return "High AR";
  if (isHighTR && TR >= UR) return "High TR";  // only if TR actually dominates
  return "UR";
}

// ─── HELPER COMPONENTS ────────────────────────────────────────────────────────
function Skeleton({ h = 24, w = "100%", r = 8 }) {
  return (
    <div style={{
      height: h, width: w, borderRadius: r,
      background: "linear-gradient(90deg,#EAF0F6 25%,#f5f8fc 50%,#EAF0F6 75%)",
      backgroundSize: "200% 100%",
      animation: "ao-shimmer 1.4s infinite",
    }} />
  );
}

function Callout({ children }) {
  return (
    <div style={{
      background: "linear-gradient(180deg,#fff9e8,#fffdf2)",
      border: "1px solid #F2D58A",
      borderRadius: 16,
      padding: "14px 16px",
      fontSize: "var(--ao-text-sm)",
      lineHeight: 1.6,
      color: T.ink,
      textAlign: "justify",
    }}>
      {children}
    </div>
  );
}

// ─── DONUT CHART ──────────────────────────────────────────────────────────────
function DonutChart({ scores, loading }) {
  const size = 260, cx = 130, cy = 130, r = 90, stroke = 28;
  const circumference = 2 * Math.PI * r;
  const keys = ["AR", "TR", "UR"];
  const colors = [T.orange, T.blue, T.gold];
  const total = keys.reduce((s, k) => s + (scores?.[k] ?? 0), 0) || 1;

  let offset = 0;
  const segments = keys.map((key, i) => {
    const frac = (scores?.[key] ?? 0) / total;
    const dash = frac * circumference;
    const seg = { key, color: colors[i], dash, gap: circumference - dash, offset: offset * circumference };
    offset += frac;
    return seg;
  });

  const displayLabel = getCategoryLabel(scores);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
      <svg viewBox={`0 0 ${size} ${size}`} width="100%" style={{ maxWidth: size }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#EAF0F6" strokeWidth={stroke} />
        {!loading && segments.map(({ key, color, dash, gap, offset: off }) => (
          <circle key={key} cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${gap}`} strokeDashoffset={circumference / 4 - off}
            strokeLinecap="butt" style={{ transition: "stroke-dasharray .8s cubic-bezier(.4,0,.2,1)" }}
          />
        ))}
        <text x={cx} y={cy - 10} textAnchor="middle" fontSize="11" fill={T.slate} fontWeight={600}>PROFILE</text>
        {loading
          ? <rect x={cx - 36} y={cy + 2} width={72} height={22} rx={6} fill="#EAF0F6" />
          : <text x={cx} y={cy + 22} textAnchor="middle" fontSize="16" fontWeight={800} fill={T.navy}>{displayLabel}</text>
        }
      </svg>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", justifyContent: "center" }}>
        {keys.map((key, i) => (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "var(--ao-text-xs)" }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: colors[i] }} />
            <span style={{ color: T.ink }}>{key}: <strong>{loading ? "—" : (scores?.[key] ?? "—")}</strong></span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CATEGORY CARD (collapsible, dominant open by default) ───────────────────
const KEY_COLOR  = { AR: T.orange, TR: T.blue, UR: T.gold, AR_TR: T.navy };
const KEY_LIGHTBG = { AR: "#fff4ed", TR: "#eef2ff", UR: "#fffbea", AR_TR: "#f0f4fb" };

function CategoryCard({ def, score, isDominant, totalScore }) {
  const [open, setOpen] = useState(isDominant);
  const color   = KEY_COLOR[def.key];
  const lightBg = KEY_LIGHTBG[def.key];
  return (
    <div style={{
      border: isDominant ? `2px solid ${color}` : `1px solid ${T.slate}55`,
      borderRadius: 16,
      overflow: "hidden",
      boxShadow: isDominant ? `0 4px 18px ${color}30` : "none",
      transition: "border-color .2s",
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "clamp(10px,2vw,14px) clamp(12px,2.5vw,18px)",
          background: open
            ? (isDominant ? `linear-gradient(90deg,${lightBg},#fff)` : `${color}08`)
            : "#fbfdff",
          border: "none", cursor: "pointer", gap: 12, textAlign: "left", transition: "background .2s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, color, fontSize: "var(--ao-text-sm)" }}>
              {def.key === "AR_TR" ? def.label : `${def.key} — ${def.label}`}
              {isDominant && (
                <span style={{ marginLeft: 8, fontSize: "var(--ao-text-xs)", background: color, color: "#fff", borderRadius: 999, padding: "2px 8px", verticalAlign: "middle", fontWeight: 700 }}>
                  Dominant
                </span>
              )}
            </div>
            <div style={{ fontSize: "var(--ao-text-xs)", color: T.slate, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{def.tagline}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {score !== null && (
            <span style={{ fontWeight: 700, fontSize: "clamp(.9rem,2vw,1.1rem)", color }}>
              {score}/{totalScore ?? 22}
            </span>
          )}
          <span style={{ color, fontSize: "1.2rem", transform: open ? "rotate(180deg)" : "none", transition: "transform .25s" }}>▾</span>
        </div>
      </button>

      {open && (
        <div style={{ padding: "clamp(10px,2vw,14px) clamp(12px,2.5vw,18px) clamp(14px,2.5vw,18px)", background: "#fff", borderTop: `1px solid ${T.line}` }}>
          <p style={{ margin: "0 0 10px", fontSize: "var(--ao-text-sm)", lineHeight: 1.65, color: T.ink, textAlign: "justify" }}>{def.overview}</p>
          {def.orgNote && (
            <p style={{ margin: "0 0 10px", fontSize: "var(--ao-text-xs)", color: T.slate, fontStyle: "italic", lineHeight: 1.6 }}>{def.orgNote}</p>
          )}
          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 700, color, fontSize: "var(--ao-text-xs)", marginBottom: 6 }}>To do:</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {def.toDo.map((item, i) => (
                <li key={i} style={{ fontSize: "var(--ao-text-xs)", color: T.ink, marginBottom: 3, lineHeight: 1.5 }}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(date) {
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

// ─── THRESHOLD CARD ───────────────────────────────────────────────────────────
function ThresholdCard({ scores, loading }) {
  const thresholds = [
    { key: "AR", label: "Achievement Related", target: "> 12", pass: (scores?.AR ?? 0) > 12, value: scores?.AR },
    { key: "TR", label: "Task Related",        target: "< 6",  pass: (scores?.TR ?? 0) < 6,  value: scores?.TR },
    { key: "UR", label: "Unrelated to Achievement", target: "< 4", pass: (scores?.UR ?? 0) < 4, value: scores?.UR },
  ];
  return (
    <div className="ao-threshold-grid">
      {thresholds.map(({ key, label, target, pass, value }) => (
        <div key={key} style={{
          background: pass ? "linear-gradient(180deg,#f0fff4,#e8fdf0)" : "linear-gradient(180deg,#fff0f0,#fdeaea)",
          border: `1px solid ${pass ? "#86efac" : "#fca5a5"}`,
          borderRadius: 14, padding: "14px 16px",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontWeight: 700, fontSize: "var(--ao-text-sm)", color: T.ink }}>{key}</span>
            <span style={{ fontSize: "1.1rem" }}>{pass ? "✅" : "⚠️"}</span>
          </div>
          <div style={{ fontSize: "var(--ao-text-xs)", color: T.slate, marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: "var(--ao-text-xs)" }}>
            <span style={{ color: T.slate }}>Ideal: </span>
            <strong style={{ color: T.ink }}>{target}</strong>
            <span style={{ color: T.slate }}> · Score: </span>
            <strong style={{ color: pass ? "#16a34a" : "#dc2626" }}>{loading ? "—" : (value ?? "—")}</strong>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function AOResults() {
  const location = useLocation();
  const navState = location.state || {};

  const [reportMeta,      setReportMeta]      = useState(null);
  const [scores,          setScores]          = useState(null);
  const [categoryProfile, setCategoryProfile] = useState(null);
  const [answers,         setAnswers]         = useState(null);
  const [insightText,     setInsightText]     = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [submissionTime,  setSubmissionTime]  = useState(null);
  const [pdfData,         setPdfData]         = useState(null);
  const [isGenerating,    setIsGenerating]    = useState(false);
  const generatingRef = useRef(false);

  const handleDownloadPDF = useCallback(() => {
    if (loading || !scores || generatingRef.current) return;
    generatingRef.current = true;
    setIsGenerating(true);
    setPdfData({
      userInfo: { name: reportMeta?.orgName || "Participant" },
      scores: scores || {},
      categoryProfile: categoryProfile || "",
      insightText: insightText || "",
      submission_time: submissionTime,
    });
  }, [loading, scores, reportMeta, categoryProfile, insightText, submissionTime]);

  const handlePdfComplete = useCallback(() => {
    setPdfData(null);
    setIsGenerating(false);
    generatingRef.current = false;
  }, []);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        // ── SCENARIO A: navigated here directly from survey submission (mirrors JH pattern)
        if (navState?.scores) {
          const aoData = navState.scores;
          const dateStr = aoData.submission_time
            ? formatDate(new Date(aoData.submission_time))
            : formatDate(new Date());

          setReportMeta({
            orgName: navState.name || aoData.userInfo?.name || aoData.name || "Participant",
            period:  dateStr,
          });
          setScores(aoData.scores ?? aoData);
          setCategoryProfile(aoData.categoryProfile);
          setAnswers(aoData.answers);
          setInsightText(buildInsight(aoData.scores ?? aoData));
          setSubmissionTime(aoData.submission_time ?? new Date().toISOString());
          return;
        }

        // ── FALLBACK: page refreshed — re-fetch via API using email / phone ──
        const email = navState.email ?? null;
        const phone = navState.phone ?? null;

        if (!email && !phone) {
          setError("No result data found. Please complete the survey first.");
          return;
        }

        const API_BASE = import.meta.env.VITE_API_URL ?? "";
        const res = await fetch(`${API_BASE}/api/calculate-score/ao`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, phone }),
        });

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody.error ?? `Server error ${res.status}`);
        }

        const json = await res.json();
        if (!json.success) throw new Error(json.error ?? "Unexpected response.");

        const aoData = json.data;
        const dateStr = aoData.submission_time
          ? formatDate(new Date(aoData.submission_time))
          : formatDate(new Date());

        setReportMeta({
          orgName: aoData.userInfo?.name || aoData.name || "Participant",
          period:  dateStr,
        });
        setScores(aoData.scores ?? aoData);
        setCategoryProfile(aoData.categoryProfile);
        setAnswers(aoData.answers);
        setInsightText(buildInsight(aoData.scores ?? aoData));
        setSubmissionTime(aoData.submission_time ?? new Date().toISOString());

      } catch (e) {
        setError(e.message || "Failed to load report data. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function buildInsight(sc) {
    if (!sc) return null;
    const label = getCategoryLabel(sc);
    const total = (sc.AR ?? 0) + (sc.TR ?? 0) + (sc.UR ?? 0);
    const arPct = total > 0 ? Math.round(((sc.AR ?? 0) / total) * 100) : 0;

    if (label === "High AR & TR") {
      return "You score highly on both Achievement Related and Task Related dimensions — an excellent profile. You act as per the requirements at the workplace, combining reliability with achievement drive and progressing naturally from structured execution to independent goal-setting.";
    }
    if (label === "High AR") {
      return `Your dominant orientation is Achievement Related (${arPct}% of responses). You have a high level of achievement motivation — you set your own goals, thrive on challenges, and are highly reliable. Freedom to work in your own style is what drives your best performance.`;
    }
    if (label === "High TR") {
      return "Your dominant orientation is Task Related. You are highly reliable and ensure work is completed within timelines under any circumstances. To grow further, seek challenges beyond assigned targets and progressively expand your independence.";
    }
    return "Your profile falls in the Unrelated to Achievement zone. This reflects a comfort-zone orientation — a cause for awareness and conscious action. The path forward begins with small challenges and a deliberate shift toward self-directed goals.";
  }

  const categoriesForDisplay = AO_DEFINITIONS.map(def => ({
    def,
    score: scores?.[def.key] ?? null,
    isDominant: categoryProfile?.includes(def.key) ?? false,
  }));

  const totalScore   = 22; // fixed: 22 questions total, each scored against exactly one dimension
  const displayLabel = getCategoryLabel(scores);

  // Keys implied by the "Your Profile" label (e.g. "High AR & TR" → AR, TR)
  const profileKeys = displayLabel === "High AR & TR" ? ["AR", "TR"]
    : displayLabel === "High AR" ? ["AR"]
    : displayLabel === "High TR" ? ["TR"]
    : displayLabel === "UR" ? ["UR"]
    : [];

  const categoriesByDominance = [...categoriesForDisplay]
    .map(c => ({ ...c, isDominant: profileKeys.includes(c.def.key) }))
    .sort((a, b) => {
      const aDom = profileKeys.includes(a.def.key);
      const bDom = profileKeys.includes(b.def.key);
      if (aDom === bDom) return (b.score ?? 0) - (a.score ?? 0);
      return aDom ? -1 : 1;
    });
  const participant    = reportMeta?.orgName || "—";
  const assessmentDate = reportMeta?.period || "—";
  
  if (loading && !scores) return (
    <div style={{ padding: "2rem", textAlign: "center", fontFamily: "'Satoshi', sans-serif" }}>Loading Report…</div>
  );
  if (error && !scores) return (
    <div style={{ padding: "2rem", color: "red", fontFamily: "'Satoshi', sans-serif" }}>Error: {error}</div>
  );

  return (
    <>
      <div className="ao-report" id="ao-report-content">
        <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />
        <style>{`
          :root {
            --ao-text-xs:   clamp(0.75rem, 0.7rem + 0.25vw, 0.875rem);
            --ao-text-sm:   clamp(0.875rem, 0.8rem + 0.35vw, 1rem);
            --ao-text-base: clamp(1rem, 0.95rem + 0.25vw, 1.125rem);
            --ao-text-lg:   clamp(1.125rem, 1rem + 0.75vw, 1.4rem);
            --ao-text-xl:   clamp(1.45rem, 1.2rem + 1.1vw, 2rem);
            --ao-space-4: 1rem; --ao-space-6: 1.5rem; --ao-space-8: 2rem;
            --ao-space-10: 2.5rem; --ao-space-12: 3rem; --ao-space-16: 4rem;
            --ao-navy: #A80D2C; --ao-blue: #C4163A; --ao-gold: #D79A1E;
            --ao-ink: #1D2733; --ao-muted: #5B677A;
            --ao-bg: #f4f6f8; --ao-card: #ffffff; --ao-line: #DCE5EF;
            --ao-radius: 20px; --ao-radius-sm: 12px;
            --ao-shadow: 0 18px 40px rgba(14,39,68,.10);
            --ao-font: 'Satoshi', sans-serif;

            /* Logo panel: fluid width clamped between 180px and 500px */
            --logo-w: clamp(180px, 42vw, 500px);
            --logo-h: clamp(90px,  21vw, 250px);
            --logo-img-h: clamp(30px, 6vw, 65px);
            --logo-pl: clamp(20px, 5vw, 60px);
          }

          /* ── Root ── */
          .ao-report {
            font-family: var(--ao-font);
            background: linear-gradient(180deg, #eef4f9 0%, #f7fafc 100%);
            color: var(--ao-ink);
            line-height: 1.55;
          }
          .ao-report .report {
            max-width: 1100px;
            margin: 0 auto;
            padding: var(--ao-space-8) var(--ao-space-4) var(--ao-space-16);
          }

          /* ── Page shells ── */
          .ao-report .page {
            background: var(--ao-card);
            border: 1px solid var(--ao-line);
            border-radius: 28px;
            box-shadow: var(--ao-shadow);
            overflow: hidden;
            margin-bottom: var(--ao-space-8);
          }

          /* ── Cover ── */
          .ao-report .cover {
            min-height: 92vh;
            position: relative;
            background: radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%),
                        linear-gradient(135deg, #A81026, #C4163A 55%, #7B0F2B);
            color: white;
          }
          .ao-report .cover-inner {
            padding: var(--ao-space-16);
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            min-height: 92vh;
          }
          .ao-report .brand {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--ao-space-4);
            position: relative;
            z-index: 2;
            padding-right: clamp(0px, 38vw, 200px);
          }
          .ao-report .logo-panel {
            position: absolute;
            top: 0;
            right: 0;
            width: var(--logo-w);
            height: var(--logo-h);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            pointer-events: none;
          }
          .ao-report .logo-panel-inner {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            position: relative;
            z-index: 2;
            padding-left: var(--logo-pl);
            padding-bottom: clamp(8px, 2vw, 25px);
          }
          .ao-report .logo-img {
            height: var(--logo-img-h);
            width: auto;
            object-fit: contain;
            flex-shrink: 0;
          }
          .ao-report .badge {
            border: 1px solid rgba(255,255,255,.18);
            background: rgba(255,255,255,.08);
            padding: clamp(.3rem,.8vw,.6rem) clamp(.5rem,1.2vw,1rem);
            border-radius: 999px;
            font-size: clamp(0.58rem,1.5vw,0.85rem);
            white-space: nowrap; flex-shrink: 0;
          }
          .ao-report .cover-title { margin-top: var(--ao-space-16); }
          .ao-report .cover-title h2 {
            margin: 0 0 var(--ao-space-4);
            font-size: clamp(2.2rem, 5vw, 4.4rem);
            line-height: .96; color: #fff;
          }
          .ao-report .cover-title p {
            font-size: var(--ao-text-lg);
            max-width: 100%;
            color: rgba(255,255,255,.84);
            text-align: justify;
          }
          .ao-report .cover-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: var(--ao-space-4);
            margin-top: var(--ao-space-12);
          }
          .ao-report .meta {
            background: rgba(255,255,255,.08);
            border: 1px solid rgba(255,255,255,.16);
            border-radius: 18px;
            padding: 1rem 1.1rem;
          }
          .ao-report .meta span   { display: block; font-size: var(--ao-text-xs); color: rgba(255,255,255,.66); margin-bottom: .25rem; }
          .ao-report .meta strong { font-size: var(--ao-text-base); color: white; }
          .ao-report .cover-badges { display: flex; flex-wrap: wrap; gap: .6rem; margin-top: var(--ao-space-8); }
          .ao-report .cover-badge {
            background: rgba(255,255,255,.12);
            border: 1px solid rgba(255,255,255,.18);
            padding: .5rem .9rem; border-radius: 999px; font-size: 0.95rem;
          }
          .ao-report .footer-note {
            display: flex; justify-content: space-between; gap: var(--ao-space-4);
            color: rgba(255,255,255,.72); font-size: var(--ao-text-xs);
            margin-top: var(--ao-space-10);
          }

          /* ── Inner pages ── */
          .ao-report .page-inner { padding: var(--ao-space-10); }
          .ao-report .topbar {
            display: flex; justify-content: space-between; align-items: flex-start;
            gap: var(--ao-space-4); margin-bottom: var(--ao-space-8);
          }
          .ao-report .section-kicker {
            color: #c62828; font-size: var(--ao-text-xs);
            text-transform: uppercase; letter-spacing: .12em; font-weight: 700;
          }
          .ao-report .section-title { margin: .35rem 0 0; font-size: var(--ao-text-xl); color: #0f2744; }
          .ao-report .pill {
            display: inline-flex; align-items: center; gap: .45rem;
            padding: .5rem .85rem; border-radius: 999px;
            font-size: var(--ao-text-xs); font-weight: 700;
            text-transform: uppercase; letter-spacing: .06em; white-space: nowrap;
          }
          .ao-report .pill-gold  { background: #fef9ed; color: #B8820F; }
          .ao-report .pill-red   { background: #fef2f2; color: #c62828; }
          .ao-report .pill-slate { background: #f1f5f9; color: #5B677A; }

          /* ── Cards ── */
          .ao-report .card {
            background: linear-gradient(180deg, #fff 0%, #fbfcfd 100%);
            border: 1px solid var(--ao-line); border-radius: var(--ao-radius);
            padding: var(--ao-space-6);
          }
          .ao-report .card h3 { margin: 0 0 .75rem; font-size: var(--ao-text-lg); color: #0f2744; }
          .ao-report .lead { color: #334155; font-size: var(--ao-text-base); margin: 0; text-align: justify; }

          /* ── Sub-panel ── */
          .ao-report .sub-panel {
            background: #fbfdff; border: 1px solid var(--ao-line);
            border-radius: 16px; padding: 1.25rem;
          }
          .ao-report .sub-panel h4 { margin: 0 0 .65rem; font-size: var(--ao-text-base); color: #0f2744; }
          .ao-report .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }

          /* ── Score bars (tall-track, VAK style) ── */
          .ao-report .bar-row { display: grid; grid-template-columns: 200px 1fr 52px; gap: 1rem; align-items: center; margin-bottom: .85rem; }
          .ao-report .bar-label { font-size: var(--ao-text-sm); font-weight: 600; color: #0f2744; }
          .ao-report .bar-label.dominant { font-weight: 700; }
          .ao-report .bar-track { height: 22px; background: #e9eef3; border-radius: 999px; overflow: hidden; }
          .ao-report .bar-fill  { height: 100%; border-radius: 999px; transition: width .8s cubic-bezier(.4,0,.2,1); }
          .ao-report .bar-val   { text-align: right; font-size: var(--ao-text-sm); font-weight: 700; font-variant-numeric: tabular-nums; }

          /* ── Score tiles ── */
          .ao-report .score-tile-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
          .ao-report .score-tile {
            border-radius: 14px; border: 1px solid var(--ao-line);
            padding: .7rem .85rem;
            background: linear-gradient(180deg,#fff,#f8fbff); min-width: 0;
          }
          .ao-report .score-tile.dominant {
            border: 2px solid ${T.gold};
            background: linear-gradient(180deg,#fffdf0,#fffbea);
          }
          .ao-report .score-tile-label { font-size: .72rem; letter-spacing: .08em; text-transform: uppercase; color: var(--ao-muted); font-weight: 700; margin-bottom: 2px; }
          .ao-report .score-tile-value { font-size: 1.7rem; font-weight: 800; line-height: 1.1; color: var(--ao-navy); }
          .ao-report .score-tile.dominant .score-tile-value { color: ${T.gold}; }
          .ao-report .score-tile-band  { font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; margin-top: 2px; }

          /* ── Threshold grid ── */
          .ao-threshold-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; }

          /* ── Trio grid (Page 2 — The Three Dimensions explainer) ── */
          .ao-report .trio-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; margin: 1rem 0 1.25rem; }
          .ao-report .trio-card { border: 1px solid var(--ao-line); border-radius: 18px; padding: 1.1rem 1.15rem; background: #fff; min-width: 0; }
          .ao-report .trio-dot-row { display: flex; align-items: center; gap: .55rem; margin-bottom: .35rem; }
          .ao-report .trio-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
          .ao-report .trio-card h3 { margin: 0; font-size: 1.05rem; color: #0f2744; }
          .ao-report .trio-sub { font-size: var(--ao-text-xs); font-weight: 700; letter-spacing: .04em; color: var(--ao-muted); text-transform: uppercase; margin-bottom: .5rem; }
          .ao-report .trio-quote { font-style: italic; font-size: var(--ao-text-xs); color: var(--ao-muted); margin-bottom: .6rem; }
          .ao-report .trio-desc { font-size: var(--ao-text-sm); color: #334155; line-height: 1.6; margin: 0 0 .75rem; }
          .ao-report .trio-ideal { font-size: var(--ao-text-xs); color: var(--ao-muted); border-top: 1px solid var(--ao-line); padding-top: .6rem; }
          .ao-report .trio-ideal span { font-weight: 700; color: #0f2744; }
          .ao-report .relation-note { background: #fbfdff; border: 1px solid var(--ao-line); border-radius: 14px; padding: 1rem 1.15rem; font-size: var(--ao-text-sm); color: #334155; line-height: 1.65; }

          /* ── Score Dashboard panel (Page 3 — ideal threshold bars) ── */
          .ao-report .ao-score-panel {
            display: block;
            column-count: 1;
            float: none;
            position: relative;
            overflow: visible;
            border: 1px solid var(--ao-line); border-radius: 16px; padding: 1.4rem 1.6rem; background: #fbfdff; margin-bottom: 1.25rem;
          }
          .ao-report .ao-score-panel h3 { margin: 0 0 .25rem; font-size: var(--ao-text-lg); color: #0f2744; }
          .ao-report .ao-score-panel .cap { font-size: var(--ao-text-xs); color: var(--ao-muted); margin-bottom: 1.25rem; line-height: 1.6; max-width: 640px; }
          .ao-report .ideal-bar-row { display: block; float: none; column-count: 1; width: 100%; box-sizing: border-box; margin-bottom: 2.25rem; }
          .ao-report .ideal-bar-row:last-child { margin-bottom: .5rem; }
          .ao-report .ideal-bar-row .head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: .5rem; }
          .ao-report .ideal-bar-row .name { font-size: var(--ao-text-sm); font-weight: 800; color: #0f2744; }
          .ao-report .ideal-bar-row .name .full { font-weight: 500; color: var(--ao-muted); font-size: var(--ao-text-xs); }
          .ao-report .ideal-bar-row .val { font-size: 1.15rem; font-weight: 900; }
          .ao-report .ideal-track { position: relative; height: 13px; background: #EAE0E1; border-radius: 7px; margin-top: 1.6rem; }
          .ao-report .ideal-fill { height: 100%; border-radius: 7px; transition: width .8s cubic-bezier(.4,0,.2,1); }
          .ao-report .ideal-marker { position: absolute; top: -5px; width: 2px; height: 23px; background: var(--ao-ink); }
          .ao-report .ideal-marker .mk-label { position: absolute; top: -18px; white-space: nowrap; font-size: .72rem; font-weight: 800; color: var(--ao-ink); transform: translateX(-50%); left: 1px; }
          .ao-report .ideal-caption { display: flex; justify-content: space-between; margin-top: .5rem; font-size: var(--ao-text-xs); color: var(--ao-muted); flex-wrap: wrap; gap: .25rem; }
          .ao-report .profile-callout { display: block; float: none; column-count: 1; position: relative; background: linear-gradient(135deg,${T.teal},${T.blue}); color: #fff; border-radius: 16px; padding: 1.4rem 1.6rem; margin-bottom: 1.25rem; }
          .ao-report .profile-callout .pc-k { font-size: .72rem; text-transform: uppercase; letter-spacing: .1em; opacity: .8; font-weight: 700; }
          .ao-report .profile-callout .pc-v { font-size: 1.4rem; font-weight: 900; margin-top: 2px; }
          .ao-report .profile-callout p { font-size: var(--ao-text-sm); line-height: 1.6; margin-top: .85rem; opacity: .95; max-width: 680px; }
          .ao-report .gap-note { display: block; float: none; column-count: 1; position: relative; background: #fff9e8; border: 1px solid #F2D58A; border-radius: 12px; padding: .9rem 1.2rem; font-size: var(--ao-text-sm); line-height: 1.6; color: #6b4a20; }

          /* ── Coaching read (Page 4 — combination callout) ── */
          .ao-report .coaching-read { border: 1px solid #CFC5C5; border-radius: 18px; padding: 1.2rem 1.7rem; background: #fffdfd; margin-top: 1.25rem; }
          .ao-report .coaching-badge { display: inline-block; border: 1px solid #DED7D7; border-radius: 999px; padding: .28rem .75rem; font-size: .72rem; line-height: 1; font-weight: 800; letter-spacing: .02em; color: var(--ao-muted); background: #fff; margin-bottom: .7rem; }
          .ao-report .coaching-read h3 { margin: 0 0 .55rem; font-size: var(--ao-text-lg); font-weight: 900; color: #111827; }
          .ao-report .coaching-read p { margin: 0 0 1.1rem; font-size: var(--ao-text-sm); line-height: 1.65; color: #455468; }
          .ao-report .coaching-read p:last-child { margin-bottom: 0; }
          .ao-report .coaching-read strong { font-weight: 800; color: #334155; }

          @media (max-width: 768px) {
            .ao-report .trio-grid { grid-template-columns: 1fr; }
          }

          /* ── Analysis items ── */
          .ao-report .analysis-item {
            border: 1px solid var(--ao-line);
            border-radius: 16px; padding: 1rem 1.1rem;
            background: #fff; margin-bottom: .75rem;
          }
          .ao-report .analysis-item.active {
            border: 2px solid ${T.gold};
            background: linear-gradient(180deg,#fffdf5,#fff);
            box-shadow: 0 0 0 3px rgba(215,154,30,.12);
          }
          .ao-report .analysis-item h3 { margin: 0 0 .5rem; font-size: var(--ao-text-sm); color: #0f2744; }
          .ao-report .analysis-item.active h3 { color: ${T.gold}; }
          .ao-report .analysis-item p  { margin: 0 0 .5rem; font-size: var(--ao-text-sm); color: #334155; line-height: 1.6; }
          .ao-report .analysis-item ul { margin: .35rem 0 0; padding-left: 18px; }
          .ao-report .analysis-item li { font-size: var(--ao-text-xs); color: #334155; margin-bottom: 3px; line-height: 1.5; }

          /* ── Back cover ── */
          .ao-report .back-cover { color: white; }
          .ao-report .back-cover .page-inner { display: flex; flex-direction: column; justify-content: space-between; }
          .ao-report .back-cover-header {
            display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
            gap: .5rem; padding: clamp(.9rem,2.5vw,1.5rem) clamp(.9rem,3vw,2.5rem);
            border-bottom: 1px solid rgba(255,255,255,0.15);
          }
          .ao-report .closing h2 { margin: 0 0 1rem; font-size: clamp(1.4rem,4vw,3rem); color: #fff; }
          .ao-report .closing p  { max-width: 100%; color: rgba(255,255,255,.84); font-size: clamp(.85rem,2vw,1.2rem); text-align: justify; }
          .ao-report .contact-grid {
            display: grid; grid-template-columns: repeat(auto-fit, minmax(min(140px,100%), 1fr));
            gap: clamp(.4rem,1.2vw,1rem); margin-top: var(--ao-space-8);
          }
          .ao-report .contact {
            background: rgba(168,16,38,.1); border: 1px solid rgba(255,255,255,.16);
            border-radius: clamp(10px,2vw,16px); padding: clamp(.6rem,1.5vw,.9rem); min-width: 0;
          }
          .ao-report .contact span   { display: block; color: rgba(255,255,255,.64); font-size: var(--ao-text-xs); margin-bottom: .3rem; }
          .ao-report .contact strong { font-size: clamp(.78rem,1.6vw,.92rem); word-break: break-word; }
          .ao-report .light { color: rgba(255,255,255,.68); }

          /* ── Download button ── */
          #ao-pdf-btn {
            position: fixed; bottom: 2rem; right: 2rem; z-index: 9999;
            display: inline-flex; align-items: center; gap: .6rem;
            padding: .75rem 1.4rem;
            background: linear-gradient(135deg, #F3AC26, #D79A1E);
            color: white; border: 1px solid #F3AC26; border-radius: 999px;
            box-shadow: 0 8px 24px rgba(198,40,40,.35), 0 0 14px rgba(243,173,38,.45);
            cursor: pointer; font-family: var(--ao-font); font-weight: 700; font-size: 1rem;
            transition: transform .18s ease, box-shadow .18s ease, opacity .18s ease;
          }
          #ao-pdf-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(198,40,40,.45); }
          #ao-pdf-btn:active:not(:disabled) { transform: translateY(0); }
          #ao-pdf-btn:disabled { opacity: .72; cursor: not-allowed; }
          #ao-pdf-btn .btn-spinner {
            width: 16px; height: 16px;
            border: 2px solid rgba(255,255,255,.35); border-top-color: white;
            border-radius: 50%; animation: ao-spin .7s linear infinite; flex-shrink: 0;
          }

          /* ── Animations ── */
          @keyframes ao-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
          @keyframes ao-fadeUp  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:none} }
          @keyframes ao-spin    { to{transform:rotate(360deg)} }
          .ao-fade-up { animation: ao-fadeUp .55s ease both; }

          /* ── Print ── */
          @media print {
            .ao-report { background: white; }
            .ao-report .report { max-width: none; padding: 0; }
            .ao-report .page { box-shadow: none; border-radius: 0; margin: 0 0 12px; border: 1px solid #ddd; }
            #ao-pdf-btn { display: none !important; }
          }

          /* ── Tablet (≤ 768px) ── */
          @media (max-width: 768px) {
            .ao-report .report { padding: 1.25rem .75rem 3rem; }
            .ao-report .page { border-radius: 20px; margin-bottom: 1.25rem; }
            


            .ao-report .cover { min-height: 88vh; }
            .ao-report .cover-inner { padding: 2.25rem; min-height: 88vh; }
            .ao-report .cover-title { margin-top: 2.5rem; }
            .ao-report .cover-title h2 { font-size: clamp(1.8rem, 5vw, 2.8rem); }
            .ao-report .cover-grid { gap: .75rem; margin-top: 1.5rem; }
            .ao-report .page-inner { padding: 1.75rem; }
            .ao-report .topbar { flex-wrap: wrap; gap: .5rem; }
            .ao-report .pill { font-size: 10px; padding: .35rem .6rem; }
            .ao-report .two-col { grid-template-columns: 1fr; }
            .ao-report .bar-row { grid-template-columns: 140px 1fr 42px; gap: .6rem; }
            .ao-report .bar-track { height: 18px; }
            .ao-threshold-grid { grid-template-columns: 1fr; }
            .ao-report .contact-grid { grid-template-columns: repeat(auto-fit, minmax(min(140px,100%),1fr)); }
          }

          /* ── Mobile (≤ 480px) ── */
          @media (max-width: 480px) {
            .ao-report .report { padding: .75rem .5rem 2.5rem; }
            .ao-report .page { border-radius: 16px; margin-bottom: .9rem; }
            .ao-report .cover { min-height: 100svh; }
            .ao-report .cover-inner { padding: 1.25rem; min-height: 100svh; }
            


            .ao-report .cover-title { margin-top: 1.5rem; }
            .ao-report .cover-title h2 { font-size: clamp(1.5rem, 7vw, 2rem); line-height: 1.08; }
            .ao-report .cover-title p { font-size: .875rem; }
            .ao-report .cover-grid { grid-template-columns: repeat(auto-fit, minmax(min(130px,100%),1fr)); gap: .5rem; margin-top: 1.1rem; }
            .ao-report .meta { padding: .6rem .75rem; }
            .ao-report .cover-badges { margin-top: .9rem; gap: .4rem; }
            .ao-report .cover-badge { font-size: .7rem; padding: .3rem .55rem; }
            .ao-report .footer-note { flex-direction: column; gap: .2rem; font-size: .68rem; margin-top: 1rem; }
            .ao-report .page-inner { padding: 1rem; }
            .ao-report .topbar { flex-wrap: wrap; gap: .4rem; margin-bottom: 1rem; }
            .ao-report .section-title { font-size: 1.25rem; }
            .ao-report .pill { font-size: 9px; padding: .25rem .5rem; }
            .ao-report .card { padding: .875rem; border-radius: 14px; }
            .ao-report .card h3 { font-size: .95rem; margin-bottom: .45rem; }
            .ao-report .lead { font-size: .84rem; }
            .ao-report .bar-row { grid-template-columns: 1fr; gap: .35rem; }
            .ao-report .bar-label { font-size: .77rem; font-weight: 600; }
            .ao-report .bar-track { height: 14px; }
            .ao-report .bar-val { font-size: .77rem; text-align: right; }
            .ao-report .score-tile-grid { grid-template-columns: repeat(3, 1fr); }
            .ao-threshold-grid { grid-template-columns: 1fr; }
            .ao-report .contact-grid { grid-template-columns: 1fr; gap: .5rem; margin-top: 1rem; }
            .ao-report .closing h2 { font-size: clamp(1.45rem,5.5vw,2rem); }
            .ao-report .closing p { font-size: .88rem; }
          }

          /* ── Tiny (≤ 390px) ── */
          @media (max-width: 390px) {
            .ao-report .report { padding: .5rem .35rem 2rem; }
            .ao-report .page { border-radius: 12px; margin-bottom: .75rem; }
           

            
            .ao-report .cover { min-height: 100svh; }
            .ao-report .cover-inner { padding: 1rem; min-height: 100svh; }
            .ao-report .brand { padding-right: clamp(0px,44vw,160px); }
            .ao-report .badge { font-size: .55rem; padding: .25rem .5rem; }
            .ao-report .cover-title { margin-top: 1rem; }
            .ao-report .cover-title h2 { font-size: clamp(1.35rem,6.5vw,1.7rem); line-height: 1.1; }
            .ao-report .cover-title p { font-size: .78rem; }
            .ao-report .cover-grid { grid-template-columns: 1fr; gap: .4rem; margin-top: .9rem; }
            .ao-report .meta { padding: .5rem .65rem; border-radius: 12px; }
            .ao-report .meta strong { font-size: .82rem; }
            .ao-report .cover-badges { margin-top: .75rem; gap: .3rem; }
            .ao-report .cover-badge { font-size: .65rem; padding: .25rem .45rem; }
            .ao-report .footer-note { flex-direction: column; gap: .15rem; font-size: .62rem; margin-top: .75rem; }
            .ao-report .page-inner { padding: .85rem; }
            .ao-report .bar-row { grid-template-columns: 1fr; gap: .3rem; }
            .ao-report .contact-grid { grid-template-columns: 1fr; gap: .4rem; margin-top: .75rem; }
          }
        `}</style>

        {/* ── Floating PDF Button ── */}
        <button
          id="ao-pdf-btn"
          onClick={handleDownloadPDF}
          disabled={isGenerating || loading || !scores}
          aria-label="Download PDF Report"
        >
          {isGenerating ? (
            <><span className="btn-spinner" />Generating PDF…</>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download PDF
            </>
          )}
        </button>

        <div className="report">

          {/* ══════════════════════════════════════════════════════
              PAGE 1 — COVER
          ══════════════════════════════════════════════════════ */}
          <section className="page cover">
            <div className="cover-inner">

              {/* Logo panel — SVG white wedge + logo image */}
               <div className="logo-panel">
                <svg
                  width="100%" height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: 'absolute', top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img src={Acumen_25_Years_LOGO} alt="Acumen Logo" className="logo-img" />
                </div>
              </div>

              <div className="brand">
                <div className="badge">Confidential Individual Report</div>
              </div>

              <div style={{ display:"flex", flexDirection:"column", justifyContent:"center", flex:1, position:"relative", zIndex:2 }}>
                <div className="cover-title">
                  <h2>Achievement <br></br> Orientation Assessment</h2>
                  <p>
                    A standardised self-evaluation instrument designed by Prayag Mehta, measuring your orientation across three dimensions — Achievement Related, Task Related, and Unrelated to Achievement.
                  </p>
                </div>

                <div className="cover-grid">
                  <div className="meta"><span>Participant</span><strong>{loading ? "—" : (reportMeta?.orgName || "—")}</strong></div>
                  <div className="meta"><span>Assessment Date</span><strong>{loading ? "—" : assessmentDate}</strong></div>
                  <div className="meta"><span>Profile</span><strong>{loading ? "—" : (displayLabel || "—")}</strong></div>
                </div>

                {/* <div className="cover-grid" style={{ marginTop:"1rem" }}>
                  <div className="meta"><span>Instrument</span><strong>Achievement Orientation (AO)</strong></div>
                  <div className="meta"><span>Report Type</span><strong>Individual Achievement Profile</strong></div>
                  <div className="meta"><span>Questions</span><strong>22 items</strong></div>
                </div> */}

                <div className="cover-badges">
                  {["Executive Dashboard","Score Breakdown","The Three Dimensions","Report Analysis"].map(b => (
                    <span key={b} className="cover-badge">{b}</span>
                  ))}
                </div>
              </div>

              <div className="footer-note" style={{ position:"relative", zIndex:2 }}>
                <div>Based on the Achievement Orientation framework designed by Prayag Mehta.</div>
                <div>Scored across 22 questions.</div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 2 — THE THREE DIMENSIONS BEHIND EVERY SCORE
          ══════════════════════════════════════════════════════ */}
          <section className="page ao-fade-up">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">The Three Dimensions Behind Every Score</h2>
                </div>
                <div className="pill pill-slate">AR · TR · UR</div>
              </div>

              <p className="lead" style={{ marginBottom:".5rem" }}>
                The Achievement Orientation framework groups how people engage with work challenges into three patterns. Every question on the assessment counts toward one of these three — understanding what each one means first makes your own scores, on the following pages, much easier to read.
              </p>

              <div className="trio-grid">
                {[
                  { key: "AR", color: T.orange, sub: "Achievement Related", quote: '"I set my own goals and strive to achieve them."', desc: "Self-driven and challenge-seeking. Sets personal goals, welcomes stretch, and needs freedom more than supervision.", ideal: "score above 12" },
                  { key: "TR", color: T.blue, sub: "Task Related", quote: '"Reliable, structured, and target-driven."', desc: "Dependable and consistent. Delivers what's assigned, works well under supervision, but rarely pushes past the target given.", ideal: "score below 6" },
                  { key: "UR", color: T.gold, sub: "Unrelated to Achievement", quote: '"Comfort-zone focused, avoiding challenges."', desc: "Focused mainly on earnings rather than the work itself. Sets few goals and tends to avoid challenge where possible.", ideal: "score below 4" },
                ].map(({ key, color, sub, quote, desc, ideal }) => (
                  <div key={key} className="trio-card">
                    <div className="trio-dot-row">
                      <div className="trio-dot" style={{ background: color }} />
                      <h3>{key}</h3>
                    </div>
                    <div className="trio-sub">{sub}</div>
                    <div className="trio-quote">{quote}</div>
                    <p className="trio-desc">{desc}</p>
                    <div className="trio-ideal">
                      Ideal for high achievement orientation: <span>{ideal}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="relation-note">
                Most people carry a mix of all three — the assessment identifies which one dominates your current responses. Later in this report, your three scores are shown against these same ideal thresholds, and your dominant orientation is explored in full, alongside the other two, on the pages that follow.
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 3 — YOUR SCORE DASHBOARD
          ══════════════════════════════════════════════════════ */}
          <section className="page ao-fade-up">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">Your Score Dashboard</h2>
                </div>
                <div className="pill pill-gold">AO Score Summary · {loading ? "—" : participant}</div>
              </div>

              <p className="lead" style={{ marginBottom:"1.25rem" }}>
                Your responses across all 22 questions, scored against each dimension's ideal threshold for a high achievement orientation.
              </p>

              {/* Score tiles */}
              <div className="sub-panel" style={{ marginBottom:"1.25rem" }}>
                <h4>Your Orientation Profile</h4>
                <div className="score-tile-grid" style={{ marginTop:".25rem" }}>
                  {categoriesByDominance.map(({ def, score, isDominant }) => {
                    const keyColor = { AR: T.orange, TR: T.blue, UR: T.gold }[def.key];
                    const lightBg  = { AR: "#fff4ed", TR: "#eef2ff", UR: "#fffbea" }[def.key];
                    return (
                      <div key={def.key} style={{
                        borderRadius: 14,
                        padding: ".7rem .85rem",
                        minWidth: 0,
                        border: isDominant ? `2px solid ${keyColor}` : `1px solid ${T.line}`,
                        background: isDominant
                          ? `linear-gradient(180deg,${lightBg},#fff)`
                          : "linear-gradient(180deg,#fff,#f8fbff)",
                      }}>
                        <div className="score-tile-label">{def.key}</div>
                        {loading
                          ? <Skeleton h={34} r={6} />
                          : <div className="score-tile-value" style={{ color: keyColor }}>{score ?? "—"}</div>
                        }
                        {isDominant && <div className="score-tile-band" style={{ color: keyColor }}>Dominant</div>}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="ao-score-panel">
                <h3>Score Breakdown</h3>
                <p className="cap">
                  Each bar shows your score out of {totalScore}. The vertical marker shows the ideal threshold for that dimension — for AR you want to be past it; for TR and UR you want to stay short of it.
                </p>

                {(() => {
                  const idealCaption = (score, threshold, direction, pass) => {
                    if (score === threshold) {
                      return direction === "above" ? "At the threshold — not yet above it" : "At the threshold — not yet under it";
                    }
                    if (pass) {
                      return direction === "above" ? `${score - threshold} points above ideal` : `${threshold - score} points below ideal`;
                    }
                    return direction === "above" ? `${threshold - score} points below ideal` : `${score - threshold} points above ideal`;
                  };
                  const idealBars = [
                    { key: "AR", label: "Achievement Related", color: T.orange, threshold: 12, direction: "above" },
                    { key: "TR", label: "Task Related", color: T.blue, threshold: 6, direction: "below" },
                    { key: "UR", label: "Unrelated to Achievement", color: T.gold, threshold: 4, direction: "below" },
                  ].map(({ key, label, color, threshold, direction }) => {
                    const score = scores?.[key] ?? 0;
                    const pass = direction === "above" ? score > threshold : score < threshold;
                    return {
                      key, label, color, threshold, direction, score, pass,
                      fillPct: Math.min(100, (score / (totalScore || 22)) * 100),
                      markerPct: Math.min(100, (threshold / (totalScore || 22)) * 100),
                      caption: idealCaption(score, threshold, direction, pass),
                    };
                  });
                  return idealBars.map(({ key, label, color, threshold, direction, score, fillPct, markerPct, caption }) => (
                    <div className="ideal-bar-row" key={key}>
                      <div className="head">
                        <span className="name">{key} <span className="full">— {label}</span></span>
                        <span className="val" style={{ color }}>{loading ? "—" : score}<span style={{ fontSize: "12px", fontWeight: 600, color: T.slate }}>/{totalScore}</span></span>
                      </div>
                      <div className="ideal-track">
                        <div className="ideal-fill" style={{ width: loading ? "0%" : `${fillPct}%`, background: color }} />
                        <div className="ideal-marker" style={{ left: `${markerPct}%` }}>
                          <span className="mk-label">Ideal: {direction === "above" ? ">" : "<"} {threshold}</span>
                        </div>
                      </div>
                      <div className="ideal-caption">
                        <span>Your score: {loading ? "—" : score}</span>
                        <span>{caption}</span>
                      </div>
                    </div>
                  ));
                })()}
              </div>

              {!loading && categoriesByDominance[0] && categoriesByDominance[1] && (
                <div className="profile-callout">
                  <div className="pc-k">Your Dominant Orientation</div>
                  <div className="pc-v">{categoriesByDominance[0].def.key} — {categoriesByDominance[0].def.label}</div>
                  <div className="pc-k" style={{ marginTop: 14 }}>Your Secondary Orientation</div>
                  <div className="pc-v" style={{ fontSize: "1.1rem" }}>{categoriesByDominance[1].def.key} — {categoriesByDominance[1].def.label}</div>
                  <p>{insightText}</p>
                </div>
              )}

              <div className="gap-note">
                {(() => {
                  const thresholds = [
                    (scores?.AR ?? 0) > 12,
                    (scores?.TR ?? 0) < 6,
                    (scores?.UR ?? 0) < 4,
                  ];
                  const passCount = thresholds.filter(Boolean).length;
                  if (passCount === 0) return "None of your three scores currently sit within the ideal range for a high achievement orientation — that's common, and it's exactly what this report is meant to surface. Review this alongside a manager or coach: the goal isn't to label where you stand today, but to identify how to channel your energy toward more self-directed goals over time.";
                  if (passCount === 3) return "All three of your scores currently sit within the ideal range for a high achievement orientation — a strong, well-rounded profile. Review this alongside a manager or coach to keep building on this foundation and sustain it over time.";
                  return `${passCount} of your three scores currently sit within the ideal range for a high achievement orientation. Review this alongside a manager or coach: the goal isn't to label where you stand today, but to identify how to channel your energy toward more self-directed goals over time.`;
                })()}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 4 — YOUR ORIENTATION  (AR / TR / UR profiles)
          ══════════════════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">Your Orientation</h2>
                </div>
                <div className="pill pill-slate">AR · TR · UR</div>
              </div>
 
              <p className="lead" style={{ marginBottom:"1.25rem" }}>
                Each dimension reflects a distinct pattern of how you perceive challenges, engage with your work environment, and direct your energy. All three are detailed below, ordered by your score — starting with your dominant orientation, followed by your secondary one. Tap any card to explore further.
              </p>
 
              <div style={{ display:"grid", gap:".75rem" }}>
                {categoriesByDominance.map(({ def, score, isDominant }) => (
                  <CategoryCard
                    key={def.key}
                    def={def}
                    score={score}
                    isDominant={isDominant}
                    totalScore={totalScore}
                  />
                ))}
              </div>

              {/* Coaching read — combination callout */}
              {!loading && categoriesByDominance[0] && categoriesByDominance[1] && (
                <div className="coaching-read">
                  <div className="coaching-badge">COACHING READ — NOT A SEPARATE SCORE</div>
                  <h3>Your Combination: {categoriesByDominance[0].def.key} + {categoriesByDominance[1].def.key}</h3>
                  <p>
                    Your two highest scores are{" "}
                    <strong>{categoriesByDominance[0].def.label} ({categoriesByDominance[0].score})</strong>{" "}
                    and{" "}
                    <strong>{categoriesByDominance[1].def.label} ({categoriesByDominance[1].score})</strong>
                    . Reviewing these together — rather than in isolation — gives the clearest picture of how you currently show up at work, and where the most natural next step for growth lies.
                  </p>
                  <p>{insightText}</p>
                </div>
              )}
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              BACK COVER
          ══════════════════════════════════════════════════════ */}
          <section className="page back-cover">
            <div style={{
              background:`radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,#A81026,#C4163A 55%,#7B0F2B)`,
              minHeight:"56vh",
            }}>
              <div className="back-cover-header">
                <div style={{ fontSize:"0.72rem", textTransform:"uppercase", letterSpacing:".12em", fontWeight:700, color:"rgba(255,255,255,0.66)" }}>
                  {/* Back Cover */}
                  </div>
                <div style={{ fontSize:"0.72rem", color:"rgba(255,255,255,0.5)", letterSpacing:".05em" }}>Acumen Business Catalyst</div>
              </div>

              <div className="page-inner">
                <div style={{ display:"flex", flexDirection:"column", justifyContent:"center", flex:1 }}>
                  <div className="closing" style={{ padding:"0.1rem" }}>
                    <h2>Activating Your Achievement Potential</h2>
                    <p>
                      Achievement orientation is not fixed. It is a dynamic that responds to awareness, deliberate practice, and the right environmental conditions. Acumen Business Catalyst uses assessments like this to turn behavioural data into focused development conversations that drive lasting change.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="contact-grid">
                    <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
                    <div className="contact"><span>Focus</span><strong>Leadership · Culture · Capability</strong></div>
                    <div className="contact"><span>Report type</span><strong>Individual Achievement Profile</strong></div>
                  </div>

                  <p className="light" style={{ marginTop:"1.5rem", fontSize:"0.75rem" }}>
                    This report is generated from the Achievement Orientation (AO) assessment framework, designed by Prayag Mehta. © 2026 Acumen Business Catalyst.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* ── Hidden PDF generator ── */}
      {pdfData && (
        <HiddenAOReport
          participantData={pdfData}
          onComplete={handlePdfComplete}
        />
      )}
    </>
  );
}